import {AfterViewInit, Component, OnDestroy, OnInit} from "@angular/core";
import {DomSanitizer} from "@angular/platform-browser";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {FileUploader} from "ng2-file-upload";
import {Subscription} from "rxjs";
import {filter} from "rxjs/operators";
import {GlobalPreferences} from "./core/helpers/global-data";
import {AuthService} from "./core/services/auth.service";
import {GeographyControllerService} from "./core/api/controllers/geography-controller.service";
import {MessageService} from "./core/services/message-service";
import {MixpanelService} from "./core/services/mixpanel.service";
import {NotificationService} from "./core/services/notification.service";
import {NotifyParentService} from "./core/services/notify-parent.service";
import {PaymentControllerService} from "./core/api/controllers/payment-controller.service";
import {LocationsService} from "./core/services/locations.service";
import {MatDialog} from "@angular/material/dialog";
import {ChangePasswordComponent} from "./features/settings/generic-account-card/components/change-password/change-password.component";
import {MAT_DATE_FORMATS} from "@angular/material/core";
import {ShopControllerService} from "./core/api/controllers/shop-controller.service";
import {AccountControllerService} from "./core/api/controllers/account-controller/account-controller.service";
import {ISubscription} from "./shared/interfaces/ISubscription";
import {ESubscriptionStatus} from "./shared/enums/ESubscriptionStatus";
import {JWT_OPTIONS, JwtHelperService} from "@auth0/angular-jwt";
import {_pt} from "./core/services/preview-text/helpers/preview-text.function";
import {SettingsService} from "./core/services/settings.service";
import {
  ChangeAccountInformationDialogComponent
} from "./features/settings/generic-account-card/components/change-account-information-dialog/change-account-information-dialog.component";
import {PushProgressService} from "./core/services/push-progress.service";

const SIDE_MENU_STORAGE_KEY = "sideMenuExpanded";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: [
    "./app.component.scss",
    "./features/register/register.component.scss",
  ],
  providers: [
    PaymentControllerService,
    GeographyControllerService,
    NotificationService,
    MatDialog,
    ShopControllerService,
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    JwtHelperService,
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: ["YYYY-MM-DD"],
        },
        display: {
          dateInput: "YYYY-MM-DD",
          monthYearLabel: "YYYY",
          dateA11yLabel: "LL",
          monthYearA11yLabel: "YYYY",
        },
      },
    },
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  userRoles = [];

  imageUploader: FileUploader;
  imageNeedsUpdate = false;
  image = null;
  imagePreviewUrl = null;
  sideMenuExpanded = localStorage.getItem(SIDE_MENU_STORAGE_KEY) !== "false";
  profilePicture;
  paymentData;
  hasMultipleLocations = false;
  name;
  currentLocation;
  childSub: Subscription;
  blankPage = false;
  subscription: Subscription;
  routerSubscription: Subscription;
  organizationSubscription: ISubscription;

  constructor(
    private messageService: MessageService,
    private authService: AuthService,
    private childNotifier: NotifyParentService,
    private notificationService: NotificationService,
    public global: GlobalPreferences,
    private dialog: MatDialog,
    private router: Router,
    private mixpanelService: MixpanelService,
    private sanitizer: DomSanitizer,
    private accountController: AccountControllerService,
    private parentNotifier: NotifyParentService,
    private locationsService: LocationsService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private pushProgress: PushProgressService,
  ) {
    this.childSub = this.childNotifier.getData().subscribe((data) => {
      if (data === "fullUpdate") {
        this.updateUserSettings();
      }
      if (data === "update") {
        this.updateUserSettings();
      }
    });

    this.subscription = this.messageService.accessMessage().subscribe((msg) => {
      if (msg) {
        this.hasMultipleLocations = true;
      }
    });
  }

  ngOnInit() {
    this.updateBlankPageState();
    this.routerSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe(() => {
      this.updateBlankPageState();
      this.pushProgress.restore("Exercise");
      this.pushProgress.restore("Workout");
    });

    if (this.fullyLoggedIn()) {
      this.updateUserSettings();
      this.pushProgress.restore("Exercise");
      this.pushProgress.restore("Workout");
    }
    this.imageUploader = new FileUploader({
      queueLimit: 1,
    });
    this.imageUploader.onAfterAddingFile = (file) => {
      this.imageNeedsUpdate = true;
      this.image =
        file.file.type.indexOf("image") >= 0 && file.file.size <= 2000000
          ? file.file
          : null;
      this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(
        window.URL.createObjectURL(file._file),
      );

      this.updateProfilePicture();
    };
  }

  toggleSideMenu() {
    this.sideMenuExpanded = !this.sideMenuExpanded;
    localStorage.setItem(SIDE_MENU_STORAGE_KEY, String(this.sideMenuExpanded));
  }

  isSuperAdmin() {
    return this.authService.checkTokenAccess("SuperAdmin");
  }

  updateProfilePicture() {
    const data = new FormData();
    data.append("file", this.image.rawFile);
    this.accountController.updateProfilePicture(data).subscribe(
      () => {
        this.notificationService.success(
          "Profile picture updated successfully.",
        );
        this.profilePicture = this.imagePreviewUrl;
        this.imagePreviewUrl = null;
        this.parentNotifier.sendData("update");
      },
      (error) => this.notificationService.error(error),
    );
  }

  getDefaultLocation(locations) {
    const selectedLocationId =  AuthService.getSelectedLocationId();
    return locations.find(({ id }) => id === selectedLocationId);
  }

  updateUserSettings() {
    this.accountController.getUserSettings().subscribe(
      (response) => {
        const userInfoString =
          localStorage.getItem("decodedToken") ||
          sessionStorage.getItem("decodedToken");
        const userInfo = JSON.parse(userInfoString);

        this.userRoles = userInfo.roles;
        this.name = response.name;
        this.locationsService.locations = response.locations;

        this.hasMultipleLocations = response.locations.length > 1;

        this.mixpanelService.identify(response.username);
        this.profilePicture = response.profilePictureUrl;

        if (response.subscription) {
          localStorage.setItem(
            "userSubscription",
            JSON.stringify(response.subscription),
          );
        }

        const locationId = AuthService.getSelectedLocationId();
        if(!locationId) {
          AuthService.setSelectedLocationId(response.locations[0].id);
        }

        const defaultLocation = this.getDefaultLocation(response.locations);
        if (defaultLocation) {
          this.currentLocation = defaultLocation.name;
        }
        this.organizationSubscription = response.subscription;
      },
      (error) => {
        this.notificationService.error(error);
        this.logout();
      },
    );
  }

  get isSubscriptionInactive(): boolean {
    return this?.organizationSubscription?.subscriptionStatus === ESubscriptionStatus.Inactive && !this.router.url.includes("/update-payment-info") && !this.router.url.includes("/upgrade-subscription") && !!AuthService.getOauthTokenFromStorage();
  }

  ngAfterViewInit() {
    const faux = document.getElementById("faux");
    this.updateBlankPageState();
    if (faux != null) {
      faux.style.width =
        Math.round(document.getElementById("nav_menu").offsetWidth) + "px";
    }
  }

  fullyLoggedIn() {
    return (
      this.authService.checkTokenAccess("User") ||
      this.authService.checkTokenAccess("OrganizationAdmin")
    );
  }

  isPaymentAdmin() {
    return this.authService.checkTokenAccess("OrganizationAdmin");
  }

  logout() {
    this.pushProgress.logout();
    this.authService.logout();
  }

  ngOnDestroy() {
    this.childSub.unsubscribe();
    this.subscription.unsubscribe();
    this.routerSubscription.unsubscribe();
  }

  private updateBlankPageState(): void {
    const routePath = this.router.url.split("?")[0];
    this.blankPage = [
      "/privacy-policy",
      "/terms-and-conditions",
      "/upgrade-subscription",
    ].includes(routePath);
  }

  changeInfo() {
    this.dialog.open(ChangeAccountInformationDialogComponent, {
      height: "90vh",
      width: "1200px",
      maxWidth: '90vw'
    });
  }

  openPasswordModal() {
    this.dialog.open(ChangePasswordComponent, {
      width: "600px",
      disableClose: true,
    });
  }

  protected readonly _pt = _pt;
}
